import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import fakerbr from 'faker-br';
import { distinctUntilChanged } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-dashboard-settings-documents',
  templateUrl: './dashboard-settings-documents.component.html',
  styleUrls: ['./dashboard-settings-documents.component.scss'],
})
export class DashboardSettingsDocumentsComponent implements OnInit {
  @ViewChild('table') table: ElementRef<HTMLTableElement> | undefined;

  documentsForm = this.formBuilder.group({
    name: [null, [Validators.required, Validators.maxLength(100)]],
    description: [null, [Validators.required, Validators.maxLength(900)]],
    roles: [null, [Validators.required]],
    frameworks: [null, []],
    required: [false, []],
    have_due_date: [false, []],
    individual: [false, []],
    show_in: [null, []],
  });

  permissionObj: PermissionValues = {
    canAdd: false,
    canChange: false,
    canDelete: false,
  };

  addDocuments = false;

  documents: any[] = [];
  document: any = undefined;

  agentsOptions: SelectItem[] = [];
  frameworkList: SelectItem[] = [];
  cardsList: SelectItem[] = [];

  visible_documents: any[] = [];

  tableSize: any = 20;

  loading = false;

  // Carbon Warning Modal properties
  showDeleteModal = false;
  showSuccessModal = false;
  showErrorModal = false;
  documentToDelete: any = null;

  showFrameworks = false;

  disableShowIn = false;
  disableIndividual = false;
  allFrameworksSelected = false;
  disableFrameworksSelect = false;

  names: { [Key: string]: string } = {
    ALL: 'Todos',
    ADM: 'Administração',
    ASSIGNOR: 'Cedente',
    SHAREHOLDER: 'Cotista',
    PROVIDER: 'Prestador de serviço',
    GUARANTOR: 'Avalista',
    REPRESENTATIVE: 'Representante',
  };

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    private router: Router,
    public signupService: SignupService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.getRequestFiles();
    this.setTableSize();
    this.setData();
    this.getRoles();
    this.getFrameworks();
    this.getCards();
    this.setPermissions();

    this.documentsForm.controls.individual.valueChanges
      .pipe(distinctUntilChanged((a: any, b: any) => a === b))
      .subscribe((value) => {
        this.documentsForm.controls.show_in.setValue(null);

        if (value) {
          this.disableShowIn = true;
        } else {
          this.disableShowIn = false;
        }

        this.documentsForm.controls.show_in.updateValueAndValidity();
      });

    this.documentsForm.controls.show_in.valueChanges.subscribe((value) => {
      if (value && value.length > 0) {
        this.documentsForm.controls.individual.setValue(false);
        this.disableIndividual = true;
      } else {
        this.disableIndividual = false;
      }

      this.documentsForm.controls.individual.updateValueAndValidity();
    });

    this.documentsForm.controls.roles.valueChanges.subscribe((value: any) => {
      let roles: any[] = [];

      if (value) {
        roles = value
          .map(
            (item: any) =>
              this.agentsOptions.find((i) => i.value === item)?.extra,
          )
          .join(', ');
      }
      if (roles.includes('PJ')) {
        this.documentsForm.controls.frameworks.setValidators([
          Validators.required,
        ]);
        this.showFrameworks = true;
      } else {
        this.documentsForm.controls.frameworks.clearValidators();
        this.showFrameworks = false;
        this.documentsForm.controls.frameworks.setValue(null);
      }
    });
  }

  async getCards() {
    try {
      const { data } = await this.api.get({
        route: 'api/registration/frontend_cards/',
        token: true,
      });

      if (data) {
        this.cardsList = data.map((item: any) => {
          return {
            label: item.name,
            value: item.id,
          };
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  async getFrameworks() {
    try {
      const res = await this.api.get({
        route: 'api/registration/framework',
        token: true,
      });

      if (res) {
        this.frameworkList = res.map((item: any) => {
          return {
            label: `${item.name} ${item.initials}`,
            value: item.id,
          };
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  toggleAllFrameworks(value: any) {
    // onCheckChanged emite true/false ou o value se definido
    const checked = typeof value === 'boolean' ? value : Boolean(value);
    this.allFrameworksSelected = checked;

    if (checked) {
      // Limpar select e deixar readonly/disabled
      this.documentsForm.controls.frameworks.setValue(null);
      this.disableFrameworksSelect = true;
      this.documentsForm.controls.frameworks.disable();
    } else {
      // Habilitar select para seleção manual
      this.disableFrameworksSelect = false;
      this.documentsForm.controls.frameworks.enable();
    }
  }

  async getRequestFiles() {
    this.loading = true;

    try {
      const res = await this.api.get({
        route: 'api/registration/file_type/',
        token: true,
      });

      const documents: { [Key: string]: any } = {};

      res.forEach((item: any) => {
        if (documents.hasOwnProperty(item.id)) {
          documents[item.id].role_names.push(item.roles.name);
        } else {
          documents[item.id] = {
            id: item.id,
            name: item.name,
            description: item.description,
            roles: item.roles,
            role_names: [item.roles.name],
            required: item.required,
            have_due_date: item.have_due_date,
            individual: item.individual,
            show_in: item.show_in,
            frameworks: item.frameworks,
          };
        }
      });

      this.documents = Object.values(documents);
      this.visible_documents = [...this.documents].splice(0, 10);
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
      console.error(error);
    }
    this.loading = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.setTableSize();
  }

  setTableSize() {
    this.tableSize = (this.table?.nativeElement.offsetWidth ?? 200) - 120;
  }

  redirect(url: string) {
    this.router.navigate([url]);
  }

  addDocumentsToggle(status: boolean) {
    this.addDocuments = status;
  }

  setDocument(document: any) {
    this.document = document;

    const frameworkIds = document.frameworks.map(
      (_framework: any) => _framework.id,
    );
    const allFrameworkValues = this.frameworkList.map((item) => item.value);

    // Verificar se todos os frameworks estão selecionados
    const allSelected =
      frameworkIds.length === allFrameworkValues.length &&
      allFrameworkValues.every((id) => frameworkIds.includes(id));

    this.allFrameworksSelected = allSelected;
    this.disableFrameworksSelect = allSelected;

    this.documentsForm.patchValue({
      name: document.name,
      description: document.description,
      roles: document.roles.map((_role: any) => _role.id),
      required: document.required,
      // Se todos estão selecionados, deixar o select vazio
      frameworks: allSelected ? null : frameworkIds,
      have_due_date: document.have_due_date,
      individual: document.individual,
      show_in: document.show_in,
    });

    if (allSelected) {
      this.documentsForm.controls.frameworks.disable();
    } else {
      this.documentsForm.controls.frameworks.enable();
    }

    this.addDocumentsToggle(true);
  }

  isAdminField(slug: string) {
    if (slug) {
      return slug.includes('ADM');
    }
    return false;
  }

  openDeleteModal(document: any) {
    this.documentToDelete = document;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.documentToDelete = null;
  }

  async confirmDeleteDocument() {
    if (!this.documentToDelete) return;

    try {
      await this.api.delete({
        route: `api/registration/file_type/${this.documentToDelete.id}/`,
        token: true,
      });

      this.documents = this.documents.filter(
        (document: any) => document.id !== this.documentToDelete.id,
      );

      this.closeDeleteModal();
      this.showSuccessModal = true;
      this.getRequestFiles();
    } catch (error) {
      this.closeDeleteModal();
      this.showErrorModal = true;
    }
  }

  closeSuccessModal() {
    this.showSuccessModal = false;
  }

  closeErrorModal() {
    this.showErrorModal = false;
  }

  // Modal methods
  getDeleteModalTitle() {
    return 'Tem certeza?';
  }

  getDeleteModalMessage() {
    return 'Você não poderá reverter isso!';
  }

  getDeleteConfirmText() {
    return 'Sim, apague!';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  getSuccessModalTitle() {
    return 'Apagado!';
  }

  getSuccessModalMessage() {
    return 'Documento apagado com sucesso';
  }

  getSuccessConfirmText() {
    return 'OK';
  }

  getErrorModalTitle() {
    return 'Erro!';
  }

  getErrorModalMessage() {
    return 'Erro ao apagar documento';
  }

  getErrorConfirmText() {
    return 'OK';
  }

  closeModal() {
    this.addDocuments = false;
    this.document = null;
    this.documentsForm.reset();
    // Resetar estado do checkbox "Todos os enquadramentos"
    this.allFrameworksSelected = false;
    this.disableFrameworksSelect = false;
    this.documentsForm.controls.frameworks.enable();
  }

  async addDocument() {
    try {
      const values = this.documentsForm.value;

      // Se "Todos os enquadramentos" estiver selecionado, enviar todos os frameworks
      const frameworksIds = this.allFrameworksSelected
        ? this.frameworkList.map((item) => item.value)
        : (values.frameworks ?? []);

      const payload = {
        roles_ids: values.roles ?? [],
        name: values.name,
        slug: values.name
          .toLowerCase()
          .replace(/[^\w\s]/gi, '')
          .split(' ')
          .join('-'),
        description: values.description,
        frameworks_ids: frameworksIds,
        required: values.required ?? false,
        have_due_date: values.have_due_date ?? false,
        individual: values.individual ?? false,
        show_in: values.show_in ?? [],
      };

      const res = this.document
        ? await this.api.put({
            route: `api/registration/file_type/${this.document.id}/`,
            token: true,
            body: payload,
          })
        : await this.api.post({
            route: 'api/registration/file_type/',
            token: true,
            body: payload,
          });

      this.toast.show('info', 'Sucesso', 'Documento cadastrado com sucesso.');

      this.closeModal();

      this.getRequestFiles();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao cadastrar o documento.',
      );
    }
  }
  async getRoles() {
    try {
      const res = await this.api.get({
        route: 'api/roles',
        token: true,
      });

      const newAgentsOptions = res.map((_agent: any) => {
        const roles = _agent.slug.split('-');

        if (roles[0].includes('ADM') || roles[0].includes('REPRESENTATIVE')) {
          return undefined;
        }

        // Adicionar filtro para is_visible
        if (!_agent.is_visible) {
          return undefined;
        }

        return {
          label: `${this.names[roles[0]]} ${_agent.name}`,
          value: _agent.id,
          extra: roles.includes('PJ') ? 'PJ' : 'PF',
        };
      });

      this.agentsOptions = newAgentsOptions.filter(
        (agent: any) => agent !== undefined,
      );
    } catch (error) {
      console.warn(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
  }

  setData() {
    this.signupService.fillFormEvent.subscribe(() => {
      this.documentsForm.patchValue({
        name: fakerbr.name.findName(),
        description: fakerbr.random.words(3),
        roles: [this.agentsOptions[0].value],
        required: false,
        frameworks: [this.frameworkList[0].value],
      });
    });
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_documents`,
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }
}
